import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputMask from "react-input-mask";
import { withRouter } from "react-router";
import compose from "recompose/compose";

import "./main.scss";
import { Typography } from "@material-ui/core";
import { createTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import randomColor from "randomcolor";
import SignatureCanvas from "react-signature-canvas";

import * as kiosk from "./api/kiosk";
import KioskStatus from "./components/KioskStatus";

const theme = createTheme({
  palette: {
    primary: { main: "#002BB8" },
    secondary: { main: "#7200B8" },
    info: { main: "#3177EA" },
  },
});

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const styles = (theme) => ({
  root: { width: "100%" },
  container: { display: "flex", flexWrap: "wrap" },
  textField: {
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit,
  },
  dense: { marginTop: 16 },
  menu: { width: 200 },
});

// Which card is open. Phone confirmation is part of each card rather than a
// step of its own.
const STEP = {
  NONE: "none",
  SIGN_IN: "signIn",
  SIGN_OUT: "signOut",
  INTAKE: "intake",
};

/**
 * Waiting-room kiosk.
 *
 * The tablet holds no credential. `GET /kiosk/today` returns the roster the
 * cards need and nothing more — never phone, email, address or treatment notes.
 * The patient's phone number, checked server-side on submit, unlocks a token
 * scoped to their one visit; the number itself is never sent to the browser.
 */
class PatientSignIn extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      loadError: null,
      visits: [],

      focusedVisit: null,
      step: STEP.NONE,
      currentTime: new Date(),

      phone: "",
      // Distinct from a generic error: drives the "N attempts remaining" hint.
      phoneError: null,
      attemptsRemaining: null,
      lockedOut: false,

      submitting: false,
      signInSnackbarOpen: false,
      signOutSnackbarOpen: false,
      errorSnackbarOpen: false,
      errorMessage: "",

      colors: randomColor({ count: 100, hue: "blue", luminosity: "bright" }),
    };
    this.handlePhoneChange = this.handlePhoneChange.bind(this);
  }

  componentDidMount() {
    document.title = "Patient Sign In | Appleby Wellness";
    // Any token left over from a previous patient must not carry across.
    kiosk.clearSession();
    this.loadRoster();
    // The roster changes as the day progresses; refresh so the tablet does not
    // need to be touched between patients.
    this.refreshTimer = setInterval(() => this.loadRoster(true), 60000);
  }

  componentWillUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  loadRoster = async (quiet = false) => {
    if (!quiet) this.setState({ loading: true, loadError: null });
    try {
      const visits = await kiosk.fetchToday();
      this.setState({ visits, loading: false, loadError: null });
    } catch (error) {
      this.setState({
        loading: false,
        loadError: error.message,
      });
    }
  };

  handlePhoneChange(event) {
    this.setState({ phone: event.target.value, phoneError: null });
  }

  /**
   * Open the card for a visit.
   *
   * Sign-in and sign-out show everything on one card — appointment details,
   * phone confirmation and signature together — and submit in one action. The
   * phone check happens server-side on submit; the patient does not experience
   * it as a separate step.
   *
   * A first visit or a due reassessment goes to the intake form instead, still
   * behind the same phone confirmation.
   */
  handleChipClick = (visit) => {
    const needsIntake =
      visit.patient.firstVisit ||
      (visit.reassessment && !visit.assessment_completed);

    this.setState({
      focusedVisit: visit,
      step: needsIntake
        ? STEP.INTAKE
        : visit.signIn.yn
        ? STEP.SIGN_OUT
        : STEP.SIGN_IN,
      phone: "",
      phoneError: null,
      attemptsRemaining: null,
      lockedOut: false,
      currentTime: new Date(),
    });
  };

  closeDialogs = () => {
    kiosk.clearSession();
    this.setState({
      step: STEP.NONE,
      focusedVisit: null,
      phone: "",
      phoneError: null,
      attemptsRemaining: null,
      lockedOut: false,
    });
  };

  /** Translate a verify failure into the right piece of UI state. */
  handleVerifyFailure(error) {
    if (error.code === "VISIT_LOCKED") {
      this.setState({
        submitting: false,
        lockedOut: true,
        attemptsRemaining: 0,
        phoneError: error.message,
      });
      return;
    }
    this.setState({
      submitting: false,
      phoneError: error.message,
      attemptsRemaining:
        error.details && typeof error.details.attemptsRemaining === "number"
          ? error.details.attemptsRemaining
          : null,
    });
  }

  /**
   * Confirm the phone number, then record the signature — one action from the
   * patient's point of view.
   *
   * The signature is checked first so a blank one never consumes an attempt
   * against the visit's lockout budget.
   */
  handleSignSubmit = async (which) => {
    const { focusedVisit, phone } = this.state;
    if (!focusedVisit || this.state.submitting) return;

    const canvas = which === STEP.SIGN_IN ? this.sigCanvas : this.sigCanvas2;
    if (!canvas || canvas.isEmpty()) {
      this.setState({
        errorSnackbarOpen: true,
        errorMessage: "Please sign in the box before continuing.",
      });
      return;
    }

    const signature = canvas.getCanvas().toDataURL("image/png", 1.0);
    this.setState({ submitting: true, phoneError: null });

    let token;
    try {
      const result = await kiosk.verifyPhone(focusedVisit._id, phone);
      token = result.token;
    } catch (error) {
      this.handleVerifyFailure(error);
      return;
    }

    try {
      if (which === STEP.SIGN_IN) {
        await kiosk.signIn(token, signature);
        this.setState({ signInSnackbarOpen: true });
      } else {
        await kiosk.signOut(token, signature);
        this.setState({ signOutSnackbarOpen: true });
      }
      kiosk.clearSession();
      this.setState({ submitting: false, step: STEP.NONE, focusedVisit: null });
      this.loadRoster(true);
    } catch (error) {
      this.setState({
        submitting: false,
        errorSnackbarOpen: true,
        errorMessage: error.message,
      });
    }
  };

  /** First visit or due reassessment: confirm the phone, then open intake. */
  handleIntakeSubmit = async () => {
    const { focusedVisit, phone } = this.state;
    if (!focusedVisit || this.state.submitting) return;

    this.setState({ submitting: true, phoneError: null });
    try {
      // Present the station token (if the tablet has one): on a reassessment
      // that mints a clinical-capable kiosk token, which the intake form uses
      // to pre-fill the prior assessment. Without a station it is simply absent
      // and the form starts blank, exactly as before.
      const result = await kiosk.verifyPhone(
        focusedVisit._id,
        phone,
        undefined,
        kiosk.readStationToken()
      );
      kiosk.storeSession(
        result.token,
        focusedVisit._id,
        !!focusedVisit.reassessment
      );
      // The intake page reads the token from sessionStorage; the :id in the URL
      // is cosmetic, since the server derives the patient from the claims.
      this.props.history.push(`/intake/${focusedVisit.patient.id}`);
    } catch (error) {
      this.handleVerifyFailure(error);
    }
  };

  minutesWithLeading(dt) {
    return (dt.getMinutes() < 10 ? "0" : "") + dt.getMinutes();
  }

  formatTime(value) {
    const date = new Date(value);
    return `${date.getHours()}:${this.minutesWithLeading(date)}`;
  }

  renderRoster() {
    const { visits } = this.state;

    if (!visits.length) {
      return (
        <Typography variant="subtitle2">
          There are no visits today. If this is not true, please refresh the page.
        </Typography>
      );
    }

    return (
      <Grid container justify="left" alignItems="left" spacing={2} style={{ width: "100%" }}>
        {[...visits]
          .sort((a, b) => (a.patient.name || "").localeCompare(b.patient.name || ""))
          .map((visit) => (
            <Grid key={visit._id} item style={{ textAlign: "center" }}>
              <Chip
                avatar={
                  <Avatar
                    style={{
                      marginRight: 10,
                      color: "#ffffff",
                      padding: 2,
                      backgroundColor: randomColor({
                        seed: visit.patient.id,
                        hue: "blue",
                        luminosity: "bright",
                      }),
                    }}
                  >
                    {(visit.patient.name || "?")[0]}
                  </Avatar>
                }
                label={
                  <div style={{ textAlign: "left" }}>
                    <Typography variant="body1">{visit.patient.name}</Typography>
                    <Typography variant="caption">
                      {visit.service[0].type}
                      {" // "}
                      {this.formatTime(visit.date.start)} -{" "}
                      {this.formatTime(visit.date.end)}
                    </Typography>
                  </div>
                }
                onClick={() => this.handleChipClick(visit)}
                variant="outlined"
                style={{
                  padding: 20,
                  height: 70,
                  width: 260,
                  fontSize: 18,
                  textAlign: "left",
                }}
                size="medium"
              />
            </Grid>
          ))}
      </Grid>
    );
  }

  /** Phone field, shared by every card. */
  renderPhoneField() {
    const { phone, phoneError, attemptsRemaining } = this.state;
    return (
      <>
        <InputMask
          mask="(999) 999-9999"
          onChange={this.handlePhoneChange}
          value={phone}
          maskChar=" "
        >
          {() => (
            <TextField
              variant="outlined"
              label="Confirm Phone"
              style={{ width: 240 }}
              error={!!phoneError}
            />
          )}
        </InputMask>
        {phoneError && (
          <Typography
            variant="caption"
            style={{ display: "block", marginTop: 8, color: "#d32f2f" }}
          >
            {phoneError}
            {typeof attemptsRemaining === "number" && attemptsRemaining > 0 && (
              <>
                {" "}
                You have {attemptsRemaining} attempt
                {attemptsRemaining === 1 ? "" : "s"} remaining.
              </>
            )}
          </Typography>
        )}
      </>
    );
  }

  /**
   * Lockout gets its own dialog rather than a snackbar: a six-second auto-hide
   * is easy to miss, and at this point the patient cannot proceed without help.
   */
  renderLockoutDialog() {
    return (
      <Dialog open={this.state.lockedOut} onClose={this.closeDialogs}>
        <DialogContent>
          <DialogContentText style={{ color: "black", minWidth: 420 }}>
            <Typography variant="h5" style={{ marginTop: 20 }}>
              Please see the front desk
            </Typography>
            <Typography variant="body1" style={{ marginTop: 20, marginBottom: 10 }}>
              We could not confirm your phone number after several attempts. Our
              staff can sign you in — please speak to the front desk and they
              will take care of it.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeDialogs} variant="contained" color="primary" disableElevation>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  /** First visit or due reassessment — confirm the phone, then open intake. */
  renderIntakeDialog() {
    const { focusedVisit, step, phone, submitting } = this.state;
    if (!focusedVisit) return null;

    return (
      <Dialog open={step === STEP.INTAKE && !this.state.lockedOut} onClose={this.closeDialogs}>
        <DialogContent>
          <DialogContentText style={{ textAlign: "center", color: "black", minWidth: 460 }}>
            <Typography variant="subtitle2" style={{ marginTop: 20 }}>
              {focusedVisit.reassessment ? "WELCOME BACK" : "WELCOME"}
            </Typography>
            <Typography variant="h4" style={{ marginTop: 20 }}>
              {focusedVisit.patient.name}
            </Typography>
            <Typography variant="subtitle1" style={{ marginTop: 30 }}>
              {focusedVisit.reassessment
                ? "Thank you for visiting Appleby Wellness! Since it has been a while since your last visit, please fill out the following forms again to get started."
                : "Thank you for visiting Appleby Wellness! Since this is your first time visiting us, please fill out the following forms to get started."}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 24 }}>
              Please confirm the phone number we have on file for you.
            </Typography>
            <div style={{ marginTop: 16 }}>{this.renderPhoneField()}</div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeDialogs} color="primary">
            Close
          </Button>
          <Button
            onClick={this.handleIntakeSubmit}
            variant="contained"
            color="primary"
            disableElevation
            disabled={!phone || submitting}
          >
            {submitting ? "Checking..." : "Begin!"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  /**
   * Sign-in / sign-out card.
   *
   * Appointment details, phone confirmation and signature on one card, matching
   * the layout the clinic asked for. Submitting confirms the phone number
   * server-side and records the signature in a single action.
   */
  renderSignDialog(which) {
    const { focusedVisit, step, currentTime, phone, submitting } = this.state;
    if (!focusedVisit) return null;
    const isSignIn = which === STEP.SIGN_IN;

    // On sign-out the clinic wants the actual arrival time, not the scheduled
    // one, so the start reads from the recorded sign-in.
    const startedAt =
      !isSignIn && focusedVisit.signIn && focusedVisit.signIn.time
        ? new Date(focusedVisit.signIn.time)
        : new Date(focusedVisit.date.start);
    const shownDate = startedAt;

    return (
      <Dialog open={step === which && !this.state.lockedOut} onClose={this.closeDialogs}>
        <DialogContent>
          <DialogContentText style={{ color: "black", minWidth: 500 }}>
            <Typography variant="subtitle2" style={{ marginTop: 20 }}>
              {isSignIn ? "WELCOME BACK" : "SIGN OUT"}
            </Typography>
            <Typography variant="h4" style={{ marginTop: 20 }}>
              {focusedVisit.patient.name}
            </Typography>

            <Grid container spacing={3} style={{ marginTop: 20 }}>
              <Grid item xs={6}>
                <Typography variant="overline">Date</Typography>
                <br />
                <Typography variant="body1">
                  {months[shownDate.getMonth()]} {shownDate.getDate()},{" "}
                  {shownDate.getFullYear()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                {this.renderPhoneField()}
              </Grid>
            </Grid>

            <Grid container spacing={3} style={{ marginTop: 20 }}>
              <Grid item xs={6}>
                <Typography variant="overline">Service</Typography>
                <br />
                <Typography variant="body1">
                  {focusedVisit.service[0].type}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="overline">Start Time</Typography>
                <br />
                <Typography variant="body1">
                  {startedAt.getHours()}:{this.minutesWithLeading(startedAt)}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="overline">End Time</Typography>
                <br />
                <Typography variant="body1">
                  {isSignIn
                    ? `${new Date(focusedVisit.date.end).getHours()}:${this.minutesWithLeading(
                        new Date(focusedVisit.date.end)
                      )}`
                    : `${currentTime.getHours()}:${this.minutesWithLeading(currentTime)}`}
                </Typography>
              </Grid>

              <Grid item xs={12} style={{ marginTop: -10 }}>
                <Typography variant="overline" style={{ marginTop: 20 }}>
                  Sign Here
                </Typography>
                <div
                  style={{
                    borderStyle: "dotted",
                    borderWidth: 2,
                    borderColor: "#D3D3D3",
                    width: 530,
                    height: 240,
                    borderRadius: 4,
                  }}
                >
                  <SignatureCanvas
                    penColor="black"
                    ref={(ref) => {
                      if (isSignIn) this.sigCanvas = ref;
                      else this.sigCanvas2 = ref;
                    }}
                    canvasProps={{
                      width: 530,
                      height: 240,
                      className: isSignIn ? "sigCanvas" : "sigCanvas2",
                    }}
                  />
                </div>
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeDialogs} color="primary">
            Close
          </Button>
          <Button
            onClick={() => this.handleSignSubmit(which)}
            variant="contained"
            color="primary"
            disableElevation
            disabled={!phone || submitting}
          >
            {submitting ? "Saving..." : isSignIn ? "Sign In" : "Sign Out"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    if (this.state.loading) {
      return (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            width: 500,
            height: 200,
            marginTop: -100,
            marginLeft: -250,
            textAlign: "center",
          }}
        >
          <Typography component="h1" style={{ fontSize: 25 }}>
            Welcome to Appleby Wellness
          </Typography>
          <Typography component="p" style={{ fontWeight: "lighter" }}>
            Loading in your information.
          </Typography>
          <CircularProgress style={{ margin: "3rem" }} />
        </div>
      );
    }

    return (
      <ThemeProvider theme={theme}>
        <div style={{ margin: 60, marginTop: 100 }}>
          <Typography variant="subtitle2" style={{ textAlign: "left" }}>
            WELCOME TO
          </Typography>
          <Typography variant="h3" style={{ textAlign: "left", marginTop: 20 }}>
            The Appleby Wellness Clinic
          </Typography>
          <Typography variant="subtitle1" style={{ textAlign: "left", marginTop: 20 }}>
            Please click on your name below to sign in or sign out.
          </Typography>
          <br />
          <hr style={{ opacity: "20%", marginTop: 30, marginBottom: 30 }} />
          <br />

          {this.state.loadError ? (
            <Typography variant="subtitle2">
              We could not load today's appointments. Please ask the front desk
              for assistance.
            </Typography>
          ) : (
            this.renderRoster()
          )}

          <Typography
            variant="subtitle1"
            style={{ textAlign: "left", marginTop: 50, opacity: 0.53 }}
          >
            Don't see your name? Please ask the front desk for assistance.
          </Typography>
        </div>

        {this.renderIntakeDialog()}
        {this.renderLockoutDialog()}
        {this.renderSignDialog(STEP.SIGN_IN)}
        {this.renderSignDialog(STEP.SIGN_OUT)}

        {/* Bottom-left station badge (Figma 3051:21). */}
        <KioskStatus />

        <Snackbar
          open={this.state.signInSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => this.setState({ signInSnackbarOpen: false })}
        >
          <MuiAlert elevation={6} variant="filled" severity="success">
            You have signed in successfully!
          </MuiAlert>
        </Snackbar>

        <Snackbar
          open={this.state.signOutSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => this.setState({ signOutSnackbarOpen: false })}
        >
          <MuiAlert elevation={6} variant="filled" severity="success">
            You have signed out successfully!
          </MuiAlert>
        </Snackbar>

        <Snackbar
          open={this.state.errorSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => this.setState({ errorSnackbarOpen: false })}
        >
          <MuiAlert elevation={6} variant="filled" severity="error">
            {this.state.errorMessage}
          </MuiAlert>
        </Snackbar>
      </ThemeProvider>
    );
  }
}

export default compose(withStyles(styles))(withRouter(PatientSignIn));
